<template>
  <div class="sp-home">
    <AppNavigation />

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-background">
        <div class="gradient-orb orb-1"></div>
      </div>

      <v-container class="hero-content py-16">
        <v-row align="center" justify="center">
          <v-col cols="12" md="6" class="text-center text-md-left pr-md-8">
            <div class="hero-eyebrow mb-6" :class="{ 'animate-in': mounted }">
              <v-icon size="16" color="#6FCF97">mdi-shield-check</v-icon>
              <span>Technicians &middot; Dispatchers &middot; Office Staff &middot; Owners</span>
            </div>

            <h1 class="hero-title mb-6" :class="{ 'slide-in-left': mounted }">
              Complete Pest Control, From Dispatch To Done.
            </h1>

            <p class="hero-subtitle mb-8" :class="{ 'slide-in-left-delayed': mounted }">
              ExtermiCloud is the operations platform built for pest control companies. Route your
              technicians, file service reports with EPA-compliant product logs, sell recurring service
              plans, and keep every customer informed, all from one command center.
            </p>

            <div class="cta-buttons mb-6" :class="{ 'fade-in-up': mounted }">
              <v-btn
                x-large
                depressed
                class="mr-4 mb-3 cta-btn-primary btn-green"
                to="/demo"
              >
                <v-icon left size="22" color="#06120C">mdi-rocket-launch</v-icon>
                Start Free Trial
              </v-btn>
            </div>

            <div class="trust-indicators" :class="{ 'fade-in': mounted }">
              <div class="trust-badge">
                <v-icon color="white" small>mdi-calendar-month-outline</v-icon>
                <span>Technician Scheduling</span>
              </div>
              <div class="trust-badge">
                <v-icon color="white" small>mdi-clipboard-check-outline</v-icon>
                <span>Service Reports</span>
              </div>
              <div class="trust-badge">
                <v-icon color="white" small>mdi-shield-refresh-outline</v-icon>
                <span>Recurring Service Plans</span>
              </div>
              <div class="trust-badge">
                <v-icon color="white" small>mdi-cloud-check</v-icon>
                <span>99.9% Uptime</span>
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="6" class="text-center">
            <div class="phone-mockup-container" :class="{ 'float-animation': mounted }">
              <div class="mockup-glow"></div>
              <div class="phone-frame">
                <div class="phone-screen">
                  <!-- Status Bar -->
                  <div class="status-bar">
                    <span class="time">9:41</span>
                    <div class="notch"></div>
                    <div class="status-icons">
                      <v-icon x-small color="white">mdi-signal</v-icon>
                      <v-icon x-small color="white">mdi-wifi</v-icon>
                      <v-icon x-small color="white">mdi-battery</v-icon>
                    </div>
                  </div>

                  <!-- App Header -->
                  <div class="app-header">
                    <v-icon color="white" size="20">mdi-menu</v-icon>
                    <div class="header-logo">
                      <v-icon color="white" size="16">mdi-shield-check</v-icon>
                      <span>ExtermiCloud</span>
                    </div>
                    <div class="header-actions">
                      <v-icon color="white" size="20">mdi-bell</v-icon>
                      <div class="notification-dot"></div>
                    </div>
                  </div>

                  <!-- Content Area -->
                  <div class="screen-content">
                    <!-- Quick Stats -->
                    <div class="stats-row">
                      <div class="stat-card">
                        <div class="stat-icon" style="background: linear-gradient(135deg, #6FCF97 0%, #3C8564 100%);">
                          <v-icon color="white" size="20">mdi-calendar-check</v-icon>
                        </div>
                        <div class="stat-value">12</div>
                        <div class="stat-label">Today</div>
                      </div>
                      <div class="stat-card">
                        <div class="stat-icon" style="background: linear-gradient(135deg, #3C8564 0%, #1F4A38 100%);">
                          <v-icon color="white" size="20">mdi-clock-outline</v-icon>
                        </div>
                        <div class="stat-value">3</div>
                        <div class="stat-label">Pending</div>
                      </div>
                      <div class="stat-card">
                        <div class="stat-icon" style="background: linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%);">
                          <v-icon color="white" size="20">mdi-trophy</v-icon>
                        </div>
                        <div class="stat-value">94%</div>
                        <div class="stat-label">Rating</div>
                      </div>
                    </div>

                    <!-- Appointment Card -->
                    <div class="appointment-section">
                      <div class="section-header-app">
                        <h3>Next Appointment</h3>
                        <v-btn x-small text color="#3C8564">View All</v-btn>
                      </div>

                      <div class="appointment-card">
                        <div class="appointment-time">
                          <div class="time-badge">10:30 AM</div>
                          <div class="appointment-duration">45 min</div>
                        </div>
                        <div class="appointment-details">
                          <div class="customer-info">
                            <div class="customer-avatar">
                              <v-icon color="white" size="18">mdi-account</v-icon>
                            </div>
                            <div>
                              <div class="customer-name">Sarah Johnson</div>
                              <div class="customer-address">
                                <v-icon x-small color="#6c757d">mdi-map-marker</v-icon>
                                <span>123 Oak Street</span>
                              </div>
                            </div>
                          </div>
                          <div class="service-info">
                            <div class="service-badge">
                              <v-icon small color="#3C8564">mdi-bug</v-icon>
                              <span>Termite Treatment</span>
                            </div>
                            <div class="service-badge">
                              <v-icon small color="#6FCF97">mdi-repeat</v-icon>
                              <span>Recurring</span>
                            </div>
                          </div>
                        </div>
                        <div class="appointment-actions">
                          <v-btn small rounded color="#3C8564" dark elevation="2">
                            <v-icon small left>mdi-navigation</v-icon>
                            Navigate
                          </v-btn>
                          <v-btn small rounded outlined color="#3C8564">
                            <v-icon small left>mdi-message</v-icon>
                            Message
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Bottom Navigation -->
                  <div class="bottom-nav">
                    <div class="nav-item active">
                      <v-icon color="#3C8564" size="22">mdi-home</v-icon>
                      <div class="nav-indicator"></div>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-calendar</v-icon>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-home-city-outline</v-icon>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-message</v-icon>
                      <div class="message-badge">3</div>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-chart-line</v-icon>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Features Section -->
    <section id="features" class="features-section">
      <v-container>
        <div class="section-header text-center mb-12">
          <div class="section-badge mb-4" :class="{ 'fade-in-up': featuresVisible }">
            <v-icon small color="#6FCF97">mdi-shield-bug</v-icon>
            <span>OPERATIONS PLATFORM</span>
          </div>
          <h2 class="section-title mb-4" :class="{ 'fade-in-up': featuresVisible }">
            Everything Your Pest Control Business Needs
          </h2>
          <p class="section-subtitle" :class="{ 'fade-in-up-delayed': featuresVisible }">
            Routes, service reports, recurring plans, payments and customer communication in one
            platform, built for licensed pest control operators.
          </p>
        </div>

        <v-row class="mb-12">
          <v-col
            v-for="(feature, index) in features"
            :key="index"
            cols="12"
            md="6"
            lg="3"
            class="mb-6"
          >
            <div
              class="feature-card-modern"
              :class="{ 'card-animate-in': featuresVisible }"
              :style="{ 'animation-delay': `${index * 0.1}s` }"
            >
              <div class="feature-card-glow" :style="{ background: feature.gradient }"></div>
              <div class="feature-card-content">
                <div class="feature-icon-modern" :style="{ background: feature.gradient }">
                  <v-icon size="32" color="white">{{ feature.icon }}</v-icon>
                  <div class="icon-ring"></div>
                </div>

                <h3 class="feature-title">{{ feature.title }}</h3>
                <p class="feature-description">{{ feature.description }}</p>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- New this season -->
    <section class="new-section">
      <v-container>
        <div class="new-head">
          <span class="new-pill">NEW</span>
          <h2 class="new-title">Built for how pest control actually works</h2>
          <p class="new-sub">The latest release goes deeper into the field, the paperwork and recurring revenue.</p>
        </div>
        <v-row>
          <v-col v-for="n in newFeatures" :key="n.title" cols="12" sm="6" lg="4">
            <div class="new-card">
              <div class="new-icon"><v-icon size="22" color="#6FCF97">{{ n.icon }}</v-icon></div>
              <h3 class="new-card-title">{{ n.title }}</h3>
              <p class="new-card-text">{{ n.text }}</p>
            </div>
          </v-col>
        </v-row>
        <div class="text-center mt-6">
          <v-btn outlined color="#6FCF97" large to="/features">
            See every feature
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </v-container>
    </section>

    <TribzzPromo />

    <!-- Final CTA Section -->
    <section class="final-cta-section">
      <div class="cta-background">
        <div class="cta-orb cta-orb-1"></div>
        <div class="cta-orb cta-orb-2"></div>
      </div>

      <v-container class="text-center">
        <div class="cta-content">
          <v-icon size="64" color="#6FCF97" class="mb-6">mdi-shield-check</v-icon>
          <h2 class="cta-title white--text mb-4">
            Ready to Stop Losing Customers?
          </h2>
          <p class="cta-subtitle mb-8">
            See how ExtermiCloud can transform your pest control business, from dispatch to done.
          </p>

          <div class="cta-buttons-final">
            <v-btn
              x-large
              depressed
              class="mr-4 mb-3 btn-green"
              style="text-transform: none; font-size: 16px; padding: 24px 44px;"
              to="/demo"
            >
              <v-icon left size="22" color="#06120C">mdi-rocket-launch</v-icon>
              Start Your Free Trial
            </v-btn>
          </div>

          <p class="cta-note mt-6">
            No credit card required &bull; 14-day free trial &bull; Cancel anytime
          </p>
        </div>
      </v-container>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import AppNavigation from '@/components/AppNavigation.vue'
import AppFooter from '@/components/AppFooter.vue'
import TribzzPromo from '@/components/TribzzPromo.vue'

export default {
  name: 'ExtermiCloudHome',
  components: { AppNavigation, AppFooter, TribzzPromo },

  data: () => ({
    mounted: false,
    featuresVisible: false,

    newFeatures: [
      { icon: 'mdi-clipboard-text-outline', title: 'Service reports & application logs', text: 'Technicians log every product applied with its EPA registration number, amount, target and area. Export the full application log as a spreadsheet whenever you need it.' },
      { icon: 'mdi-map-marker-path', title: 'Today\'s route', text: 'Every stop in order or grouped by zone, with gate codes, pets and open pest issues. Open the whole route in Maps with one tap.' },
      { icon: 'mdi-shield-refresh-outline', title: 'Service plans that bill themselves', text: 'Sell quarterly general pest, yearly termite protection or seasonal mosquito packages. Stripe handles the renewals.' },
      { icon: 'mdi-alarm-light-outline', title: 'Urgent queue', text: 'Urgent pest reports land in a live queue that shows which technician is free soonest, so you can dispatch in seconds.' },
      { icon: 'mdi-flask-outline', title: 'Chemical inventory', text: 'Track stock with EPA numbers, active ingredients and SDS links. Product used on a job comes off inventory by itself.' },
      { icon: 'mdi-calendar-refresh-outline', title: 'Treatment-due reminders', text: 'Reminders work from each property\'s last treatment date, so customers hear from you right when the next visit is due.' },
    ],

    features: [
      {
        icon: 'mdi-map-marker-path',
        gradient: 'linear-gradient(135deg, #6FCF97 0%, #3C8564 100%)',
        title: 'Routes & Dispatch',
        description: 'Each technician\'s day in route order, with access notes, pets and one-tap directions.'
      },
      {
        icon: 'mdi-clipboard-check-outline',
        gradient: 'linear-gradient(135deg, #3C8564 0%, #1F4A38 100%)',
        title: 'Service Reports',
        description: 'Products applied with EPA numbers, findings, photos and a customer signature, emailed on the spot.'
      },
      {
        icon: 'mdi-shield-refresh-outline',
        gradient: 'linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%)',
        title: 'Recurring Service Plans',
        description: 'Monthly, quarterly or yearly plans billed automatically, with free re-services built in.'
      },
      {
        icon: 'mdi-calendar-cursor',
        gradient: 'linear-gradient(135deg, #4A4D54 0%, #2C2E33 100%)',
        title: 'Online Booking',
        description: 'Customers pick a service, see the price and choose the property. Prep instructions go out automatically.'
      },
      {
        icon: 'mdi-home-search',
        gradient: 'linear-gradient(135deg, #6FCF97 0%, #3C8564 100%)',
        title: 'Property Records',
        description: 'Treatment history, warranties, contracts, pets and access notes for every site.'
      },
      {
        icon: 'mdi-bug-outline',
        gradient: 'linear-gradient(135deg, #3C8564 0%, #1F4A38 100%)',
        title: 'Pest Sightings & Callbacks',
        description: 'Customers report pests with photos. Warranty re-services are checked for coverage automatically.'
      },
      {
        icon: 'mdi-cash-register',
        gradient: 'linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%)',
        title: 'Payments & Invoicing',
        description: 'Card on file, deposits, pay links, refunds and trip charges, all tied to the property.'
      },
      {
        icon: 'mdi-robot-outline',
        gradient: 'linear-gradient(135deg, #4A4D54 0%, #2C2E33 100%)',
        title: 'Automated Reminders',
        description: 'Treatment-due nudges, renewal reminders and seasonal campaigns that send themselves.'
      }
    ]
  }),

  mounted() {
    setTimeout(() => {
      this.mounted = true;
    }, 100);

    this.setupScrollAnimations();
  },

  methods: {
    setupScrollAnimations() {
      const observerOptions = {
        threshold: 0.15,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.target.classList.contains('features-section')) {
            this.featuresVisible = true;
          }
        });
      }, observerOptions);

      setTimeout(() => {
        const section = document.querySelector('.features-section');
        if (section) observer.observe(section);
      }, 500);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

* {
  box-sizing: border-box;
}

.sp-home {
  --bg-page: #06090a;
  --bg-alt: #0d1210;
  --bg-card: #11151a;
  --bg-card-hover: #171c1e;
  --border-subtle: rgba(255, 255, 255, 0.09);
  --border-green: rgba(111, 207, 151, 0.35);
  --text-primary: #EAF3EC;
  --text-secondary: rgba(234, 243, 236, 0.68);
  --text-muted: rgba(234, 243, 236, 0.48);
  --green: #3C8564;
  --green-light: #6FCF97;
  --green-dark: #1F4A38;

  overflow-x: hidden;
  background: var(--bg-page);
  padding-top: 80px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--text-primary);
}

.sp-home h1,
.sp-home h2,
.sp-home h3,
.sp-home .hero-title,
.sp-home .section-title,
.sp-home .cta-title {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.btn-green {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
  font-size: 14.5px;
  border-radius: 10px !important;
  background: linear-gradient(150deg, var(--green-light) 0%, var(--green) 60%, var(--green-dark) 100%) !important;
  color: #06120C !important;
  box-shadow: 0 6px 18px rgba(60, 133, 100, 0.28) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.btn-green:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(60, 133, 100, 0.38) !important;
}

/* ==================== HERO SECTION ==================== */
.hero-section {
  position: relative;
  min-height: 92vh;
  background: linear-gradient(160deg, #0d1210 0%, #06090a 55%, #050605 100%);
  display: flex;
  align-items: center;
  overflow: hidden;
  padding-top: 60px;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  opacity: 0.3;
}

.orb-1 {
  width: 560px;
  height: 560px;
  background: radial-gradient(circle, rgba(111, 207, 151, 0.5) 0%, transparent 70%);
  top: -180px;
  right: -160px;
}

.hero-content {
  position: relative;
  z-index: 2;
  color: white;
}

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
  opacity: 0;
  transform: translateY(-8px);
  transition: all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.hero-eyebrow.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.hero-title {
  font-size: 3.25rem;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.02em;
  opacity: 0;
  transform: translateX(-24px);
  transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s;
}

.hero-title.slide-in-left {
  opacity: 1;
  transform: translateX(0);
}

.hero-subtitle {
  font-size: 1.15rem;
  line-height: 1.6;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.72);
  opacity: 0;
  transform: translateX(-24px);
  transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s;
}

.hero-subtitle.slide-in-left-delayed {
  opacity: 0.95;
  transform: translateX(0);
}

.cta-buttons {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease-out 0.6s;
}

.cta-buttons.fade-in-up {
  opacity: 1;
  transform: translateY(0);
}

.cta-btn-primary {
  font-size: 16px;
  padding: 26px 34px !important;
}

.trust-indicators {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-top: 30px;
  opacity: 0;
  transition: all 0.8s ease-out 0.8s;
}

.trust-indicators.fade-in {
  opacity: 1;
}

.trust-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
}

/* Phone Mockup */
.phone-mockup-container {
  position: relative;
  display: inline-block;
  opacity: 0;
  transform: translateY(50px);
  transition: all 1s ease-out 0.4s;
}

.phone-mockup-container.float-animation {
  opacity: 1;
  transform: translateY(0);
  animation: gentle-float 6s infinite ease-in-out;
}

@keyframes gentle-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

.mockup-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  background: radial-gradient(circle, rgba(111, 207, 151, 0.25) 0%, transparent 70%);
  filter: blur(40px);
  animation: pulse-glow 4s infinite ease-in-out;
}

@keyframes pulse-glow {
  0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.1); }
}

.phone-frame {
  position: relative;
  width: 340px;
  height: 690px;
  background: #1a1a1a;
  border-radius: 45px;
  padding: 12px;
  box-shadow:
    0 0 0 2px #2a2a2a,
    0 0 0 8px #1a1a1a,
    0 30px 80px rgba(0, 0, 0, 0.5);
}

.phone-screen {
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 35px;
  overflow: hidden;
  position: relative;
}

.status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 20px;
  background: linear-gradient(135deg, #3C8564 0%, #6FCF97 100%);
  color: white;
  font-size: 13px;
  font-weight: 600;
  position: relative;
}

.time { flex: 1; }

.notch {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 140px;
  height: 22px;
  background: #1a1a1a;
  border-radius: 0 0 20px 20px;
}

.status-icons {
  display: flex;
  gap: 6px;
  align-items: center;
}

.app-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: linear-gradient(135deg, #3C8564 0%, #1F4A38 100%);
  color: white;
}

.header-logo {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 700;
}

.header-actions { position: relative; }

.notification-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: #6FCF97;
  border-radius: 50%;
  border: 2px solid white;
}

.screen-content {
  height: calc(100% - 120px);
  overflow-y: auto;
  background: #f8f9fa;
  padding: 16px;
}

.screen-content::-webkit-scrollbar { width: 0; }

.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 8px;
}

.stat-value {
  font-size: 20px;
  font-weight: 800;
  color: #212529;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 11px;
  color: #6c757d;
  font-weight: 600;
}

.appointment-section { margin-bottom: 16px; }

.section-header-app {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-header-app h3 {
  font-size: 16px;
  font-weight: 700;
  color: #212529;
  margin: 0;
  font-family: inherit;
}

.appointment-card {
  background: white;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.appointment-time {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
}

.time-badge {
  font-size: 18px;
  font-weight: 700;
  color: #3C8564;
}

.appointment-duration {
  font-size: 12px;
  color: #6c757d;
  background: #f8f9fa;
  padding: 4px 10px;
  border-radius: 12px;
}

.appointment-details { margin-bottom: 12px; }

.customer-info {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}

.customer-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3C8564 0%, #1F4A38 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.customer-name {
  font-size: 15px;
  font-weight: 700;
  color: #212529;
  margin-bottom: 4px;
}

.customer-address {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6c757d;
}

.service-info {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.service-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f8f9fa;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #495057;
}

.appointment-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.bottom-nav {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 12px 20px 16px;
  background: white;
  border-top: 1px solid #e9ecef;
}

.nav-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
}

.nav-item.active { color: #3C8564; }

.nav-indicator {
  width: 6px;
  height: 6px;
  background: #3C8564;
  border-radius: 50%;
  position: absolute;
  bottom: -8px;
}

.message-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 16px;
  height: 16px;
  background: #3C8564;
  color: white;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
}

/* ==================== FEATURES SECTION ==================== */
.features-section {
  padding: 100px 0 80px;
  background: var(--bg-page);
  position: relative;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: rgba(111, 207, 151, 0.12);
  border: 1px solid var(--border-green);
  border-radius: 30px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--green-light);
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s ease-out;
}

.section-badge.fade-in-up {
  opacity: 1;
  transform: translateY(0);
}

.section-title {
  font-size: 3rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--text-primary);
  line-height: 1.2;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s ease-out 0.1s;
}

.section-title.fade-in-up {
  opacity: 1;
  transform: translateY(0);
}

.section-subtitle {
  font-size: 1.15rem;
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s ease-out 0.2s;
  font-family: 'Inter', sans-serif;
}

.section-subtitle.fade-in-up-delayed {
  opacity: 1;
  transform: translateY(0);
}

.feature-card-modern {
  position: relative;
  background: var(--bg-card);
  border-radius: 18px;
  padding: 40px 32px;
  height: 100%;
  overflow: hidden;
  border: 1px solid var(--border-subtle);
  opacity: 0;
  transform: translateY(30px) scale(0.95);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.feature-card-modern.card-animate-in {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.feature-card-modern:hover {
  transform: translateY(-6px);
  background: var(--bg-card-hover);
  border-color: var(--border-green);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
}

.feature-card-modern:hover .feature-card-glow { opacity: 0.15; }

.feature-card-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
}

.feature-card-content {
  position: relative;
  z-index: 2;
}

.feature-icon-modern {
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

.icon-ring {
  position: absolute;
  top: -8px;
  left: -8px;
  right: -8px;
  bottom: -8px;
  border: 2px solid;
  border-radius: 24px;
  opacity: 0.2;
  border-color: inherit;
  animation: pulse-ring 3s infinite ease-in-out;
}

@keyframes pulse-ring {
  0%, 100% { transform: scale(1); opacity: 0.2; }
  50% { transform: scale(1.1); opacity: 0.1; }
}

.feature-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 12px;
  line-height: 1.3;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
}

.feature-description {
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.6;
  font-family: 'Inter', sans-serif;
}

/* ==================== FINAL CTA SECTION ==================== */
.final-cta-section {
  position: relative;
  padding: 120px 0;
  background: linear-gradient(160deg, #0d1210 0%, #06090a 100%);
  overflow: hidden;
}

.cta-background {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.cta-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.25;
}

.cta-orb-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(111, 207, 151, 0.5) 0%, transparent 70%);
  top: -150px;
  left: -120px;
}

.cta-orb-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(60, 133, 100, 0.5) 0%, transparent 70%);
  bottom: -140px;
  right: -100px;
}

.cta-content {
  position: relative;
  z-index: 2;
  max-width: 640px;
  margin: 0 auto;
}

.cta-title {
  font-size: 2.75rem;
  font-weight: 600;
}

.cta-subtitle {
  font-size: 1.15rem;
  color: rgba(255, 255, 255, 0.72);
  font-family: 'Inter', sans-serif;
}

.cta-note {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Inter', sans-serif;
}

@media (max-width: 960px) {
  .hero-title { font-size: 2.4rem; }
  .section-title { font-size: 2.2rem; }
}

/* ==================== NEW THIS SEASON ==================== */
.new-section {
  padding: 30px 0 80px;
  background: var(--bg-page);
}
.new-head {
  text-align: center;
  max-width: 720px;
  margin: 0 auto 36px;
}
.new-pill {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(111, 207, 151, 0.14);
  border: 1px solid var(--border-green);
  color: #6FCF97;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
}
.new-title {
  font-size: 2.4rem;
  line-height: 1.2;
  color: var(--text-primary);
  margin: 14px 0 10px;
}
.new-sub {
  color: var(--text-secondary);
  font-size: 1.05rem;
  margin: 0;
}
.new-card {
  height: 100%;
  padding: 24px;
  border-radius: 16px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  transition: border-color 0.2s ease, transform 0.2s ease;
}
.new-card:hover {
  border-color: var(--border-green);
  transform: translateY(-2px);
}
.new-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(111, 207, 151, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}
.new-card-title {
  font-size: 1.1rem;
  color: var(--text-primary);
  margin: 0 0 8px;
}
.new-card-text {
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
  font-size: 0.95rem;
}
@media (max-width: 600px) {
  .new-title {
    font-size: 1.8rem;
  }
}
</style>